/**
 * Кнопка «Что нового» в подвале рельса.
 *
 * Ведёт на `/releases`. Пока самый свежий релиз не открыт — иконка в акцентном
 * цвете и точка с расходящимся кольцом (см. ReleasesButton.module.css).
 * Отметка о прочитанном хранится в персональных настройках (`ui:releasesSeen`),
 * то есть переезжает вместе с пользователем на другой браузер, а не живёт
 * только в localStorage этой машины.
 */
import { useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import { NotificationOutlined } from '@ant-design/icons';
import { getReleasesSeen, subscribeReleasesSeen } from '../../shared/prefs/store.ts';
import { LATEST_RELEASE } from './releases.ts';
import styles from './ReleasesButton.module.css';

export function ReleasesButton({
  className,
  placement = 'top',
}: {
  className?: string;
  placement?: TooltipPlacement;
}): JSX.Element | null {
  const navigate = useNavigate();
  const seen = useSyncExternalStore(subscribeReleasesSeen, getReleasesSeen);

  if (!LATEST_RELEASE) return null;

  const hasUnread = LATEST_RELEASE.id !== seen;
  const tooltip = hasUnread ? `Что нового: ${LATEST_RELEASE.title}` : 'Что нового';

  return (
    <Tooltip title={tooltip} placement={placement}>
      <button
        type="button"
        className={`${className ?? ''} ${styles.button}`}
        aria-label={tooltip}
        data-testid="releases-button"
        data-unread={hasUnread ? 'true' : 'false'}
        onClick={() => navigate('/releases')}
      >
        <span className={`${styles.icon} ${hasUnread ? styles.iconUnread : ''}`}>
          <NotificationOutlined />
        </span>
        {hasUnread ? <span className={styles.dot} data-testid="releases-unread-dot" /> : null}
      </button>
    </Tooltip>
  );
}
